import { $isCodeNode } from '@lexical/code';
import { $isTableNode } from '@lexical/table';
import {
  $createParagraphNode,
  $getRoot,
  $isElementNode,
  LexicalEditor,
  LexicalNode,
} from 'lexical';
import { $isCollapsibleContainerNode } from '../plugins/CollapsiblePlugin/CollapsibleContainerNode';

// root.selectEnd() 는 getLastDescendant() 로 내려가므로 문서 끝이 표면 캐럿이 표의 마지막 셀에 잡히고,
// 코드블록·접기도 내부 컨테이너로 들어가 그 뒤에 이어 쓸 수 없음. 데코레이터(이미지·임베드)는 캐럿 자체를 못 받음.
const $needsTrailingParagraph = (node: LexicalNode | null): boolean =>
  node === null ||
  !$isElementNode(node) ||
  $isTableNode(node) ||
  $isCodeNode(node) ||
  $isCollapsibleContainerNode(node);

/** 문서 맨 끝으로 캐럿 이동. 끝이 표/코드블록/접기/데코레이터면 뒤에 빈 문단을 만들어 그 안으로. */
export const $selectDocumentEnd = (): void => {
  const root = $getRoot();
  if (!$needsTrailingParagraph(root.getLastChild())) {
    root.selectEnd();
    return;
  }
  const paragraph = $createParagraphNode();
  root.append(paragraph);
  paragraph.select();
};

/**
 * 콘텐츠 아래 빈 영역 클릭인지 판정.
 * 스크롤러 여백뿐 아니라 본문(ContentEditable) 자체의 padding·min-height 잔여 영역도 포함 —
 * 그 영역은 브라우저 기본 동작이 캐럿을 가장 가까운 텍스트(표 마지막 셀)로 보냄.
 */
export const isClickBelowContent = (
  editor: LexicalEditor,
  target: HTMLElement,
  clientY: number,
): boolean => {
  // 컨테이너가 직접 히트된 경우만 여백 클릭. 리사이저·드래그핸들 등 겹친 오버레이 클릭은 제외
  if (
    !target.classList.contains('editor-scroller') &&
    !target.classList.contains('editor') &&
    !target.classList.contains('ContentEditable__root')
  ) {
    return false;
  }
  const lastKey = editor.read(
    () => $getRoot().getLastChild()?.getKey() ?? null,
  );
  const lastElement = lastKey === null ? null : editor.getElementByKey(lastKey);
  if (lastElement === null) return true;
  return clientY > lastElement.getBoundingClientRect().bottom;
};
